import React, { useEffect, useState } from 'react';
import { colors } from '../../../styleSheet';
import {
  TableCell,
  TableRow,
  IconButton,
  Typography,
  Tooltip,
  Box,
  Link,
} from '@mui/material';
import { ContentCopy, Search, Warning } from '@mui/icons-material';

import MultiProgressBar from './MultiProgressBar';
import { CellDrawer } from './CellDrawer';
import { format } from 'date-fns';
import { UI } from '../tokens';

export function MonitoringTableRow(props) {
  const {
    session,
    chain,
    store,
    storeName,
    plannedFor,
    objectData,
    selectedDate,
    test,
    showTestSessions,
    alertInfo,
    navigationReportSentTimestamp = '-',
    signBlob,
    signedblob,
    isLoadingSignedBlob,
  } = props;
  const [selectedObjectIndex, setSelectedObjectIndex] = useState(-1);
  const [selectedObject, setSelectedObject] = useState(undefined);
  const [openAlert, setOpenAlert] = useState(false);
  const [selectedReportBlob, setSelectedReportBlob] = useState();

  useEffect(() => {
    if (selectedObjectIndex !== -1) {
      setSelectedObject(objectData[selectedObjectIndex]);
    } else {
      setSelectedObject(undefined);
    }
  }, [selectedObjectIndex, objectData]);

  useEffect(() => {
    if (!isLoadingSignedBlob && signedblob && selectedReportBlob) {
      window.open(signedblob, '_blank');
      setSelectedReportBlob(null);
    }
  }, [isLoadingSignedBlob, signedblob]);

  const handleGoReport = (reportBlobPath) => {
    setSelectedReportBlob(reportBlobPath);
    signBlob({ blobPath: reportBlobPath, store });
  };

  const onCellClick = (index) => {
    if (index === selectedObjectIndex) {
      setOpenAlert(false);
      setSelectedObjectIndex(-1);
    } else {
      setSelectedObjectIndex(index);
      setOpenAlert(false);
    }
  };

  if ((showTestSessions && test) || !test) {
    return [
      <TableRow
        key={session}
        sx={{
          backgroundColor: alertInfo ? colors.warningSurface : null,
          transition: 'background-color .15s ease',
          '& > td': { borderBottom: `1px solid ${UI.border}` },
        }}
      >
        <TableCell key={chain} align="center">
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: colors.primary }}>
            {chain}
          </Typography>
        </TableCell>
        <TableCell key={store} align="center">
          <Typography sx={{ fontSize: 14, color: colors.textGray }}>
            {store}
          </Typography>
        </TableCell>

        <TableCell key={session} align="center">
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 13,
                color: colors.primary,
              }}
            >
              {session}
            </Typography>
            <Box sx={{ display: 'flex' }}>
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(session);
                }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                href={`/pipeline_processing/${session}/${selectedDate}`}
              >
                <Search fontSize="small" />
              </IconButton>
              {alertInfo ? (
                <Tooltip
                  title={`${alertInfo.incident.metric.displayName} at ${alertInfo?.incident?.metric?.labels.session_id} 
                    of store ${alertInfo?.incident?.metric?.labels.store_code} is above the threshold ${alertInfo.incident.threshold_value} 
                    with a value of ${alertInfo.incident?.observed_value}.`}
                >
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => {
                      setOpenAlert(!openAlert);
                      setSelectedObjectIndex(-1);
                      setSelectedObject(undefined);
                    }}
                  >
                    <Warning fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
        </TableCell>

        <TableCell key={plannedFor} sx={{ textAlign: 'center' }}>
          {plannedFor ? (
            <Typography sx={{ fontSize: 14, color: colors.textGray }}>
              {format(new Date(plannedFor), 'HH:mm')}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 14, color: colors.textGray }}>-</Typography>
          )}
        </TableCell>
        {objectData.map((objectDict, index) => {
          return (
            <TableCell
              align="center"
              onClick={() => onCellClick(index)}
              key={`${session}-${objectDict['objectId']}`}
              height={20}
              width={150}
              sx={{
                backgroundColor:
                  alertInfo &&
                  parseInt(alertInfo.incident.metric.labels.object_id) ===
                    parseInt(objectDict['objectId'])
                    ? colors.warningAccent
                    : selectedObjectIndex === index
                    ? UI.border
                    : null,

                '&:hover': {
                  cursor: 'pointer',
                  backgroundColor:
                    alertInfo &&
                    parseInt(alertInfo.incident.metric.labels.object_id) ===
                      parseInt(objectDict['objectId'])
                      ? colors.warningAccentDark
                      : selectedObjectIndex === index
                      ? UI.surfaceMuted
                      : UI.surfaceMuted,
                },
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'row',
                }}
              >
                <MultiProgressBar
                  progresses={objectDict['progressValues']}
                  progressNames={objectDict['processNames']}
                  height={15}
                  maxValue={objectDict['maxValue']}
                  alertInfo={alertInfo}
                  handleGoReport={() =>
                    handleGoReport(objectDict['reportBlobPath'])
                  }
                  isLoadingSignedBlob={
                    isLoadingSignedBlob &&
                    selectedReportBlob === objectDict['reportBlobPath']
                  }
                  navigationReportSentTimestamp={
                    objectDict['navigationReportSentTimestamp']
                  }
                />
              </div>
            </TableCell>
          );
        })}
      </TableRow>,

      selectedObject || openAlert ? (
        <TableRow
          key={`drawer-${session}`}
          style={{
            background: alertInfo
              ? parseInt(alertInfo.incident.metric.labels?.object_id) ===
                parseInt(selectedObject?.objectId)
                ? colors.warningAccent
                : colors.warningSurface
              : UI.border,
          }}
        >
          <TableCell colSpan={3 + objectData.length}>
            {selectedObject ? (
              <CellDrawer
                progressValues={selectedObject['progressValues']}
                processNames={selectedObject['processNames']}
                maxValue={selectedObject['maxValue']}
              />
            ) : (
              <Box>
                <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>
                  {alertInfo.incident.condition.displayName} Alert at{' '}
                  {alertInfo.incident.metric.labels.session_id}
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  {`${alertInfo.incident.metric.displayName} at ${alertInfo.incident.metric.labels.session_id} 
                   of store ${alertInfo.incident.metric.labels.store_code} (ID: ${alertInfo.incident.metric.labels.store_id}) is above the threshold ${alertInfo.incident.threshold_value} 
                  with a value of ${alertInfo.incident.observed_value}`}
                </Typography>
                <Link href={alertInfo.incident.url} underline="always">
                  <Typography sx={{ fontSize: 13 }}>Go to incident</Typography>
                </Link>
              </Box>
            )}
          </TableCell>
        </TableRow>
      ) : undefined,
    ];
  } else {
    return null;
  }
}
